import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/shared/lib/supabase";
import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import FullscreenLayout from "@/pages/layouts/FullScreenLayout";
import { AUTH_ROUTES } from "@/shared/constants/paths";
import { SecurityNote } from "@/shared/components/ui/security-note";

const WelcomeBackPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  const handleSubmit = async () => {
    if (!email) {
      setValidationError("Enter your email address");
      return;
    }

    if (!email.includes("@")) {
      setValidationError("Enter a valid email address");
      return;
    }

    setLoading(true);
    setValidationError("");
    setServerError("");
    setSuccess("");

    try {
      const genericMessage =
        "If an account with that email exists, we've sent you a password reset link.";

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.trim().toLowerCase(),
        {
          redirectTo: `${window.location.origin}/reset-password`,
        },
      );

      if (resetError) {
        console.error("Reset password error:", resetError);
        setServerError("The reset link wasn't sent. Try again.");
      } else {
        setSuccess(genericMessage);
      }
    } catch (err) {
      console.error("Password reset error:", err);
      setServerError("We couldn't send the link. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(AUTH_ROUTES.SIGN_IN);
  };

  if (success) {
    return (
      <FullscreenLayout
        className="bg-[#f6f2f1] overflow-hidden"
        childrenContainerClassName="h-screen flex items-center justify-center p-8"
      >
        <div className="w-[600px] max-w-[90vw] bg-white rounded-2xl shadow-[0_20px_50px_rgba(12,18,28,0.1)] overflow-hidden">
          <div className="p-12">
            <h1 className="text-3xl font-medium text-stone-900 mb-4 text-center leading-tight">
              Check your email
            </h1>
            <p className="text-stone-600 mb-8 text-center">{success}</p>
            <Button onClick={handleBack} className="w-full h-12">
              Back to log in
            </Button>
          </div>
        </div>
      </FullscreenLayout>
    );
  }

  return (
    <FullscreenLayout
      className="bg-[#f6f2f1] overflow-hidden"
      childrenContainerClassName="h-screen flex items-center justify-center p-8"
    >
      <div className="w-[600px] max-w-[90vw] bg-white rounded-2xl shadow-[0_20px_50px_rgba(12,18,28,0.1)] overflow-hidden">
        <div className="p-12">
          {/* Content */}
          <h1 className="text-3xl font-medium text-stone-900 mb-6 leading-tight">
            Welcome back to Elayne
          </h1>

          <p className="text-stone-600 mb-8">Set a password to keep using your account</p>

          <div className="space-y-6 mb-8">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setValidationError("");
                }}
                placeholder="Enter your email"
                className={`h-[48px]${validationError ? " border-destructive border-[2px]" : ""}`}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
              <FieldError message={validationError || serverError} className="mt-1" />
            </div>
          </div>

          <Button onClick={handleSubmit} disabled={loading} className="w-full h-12">
            {loading ? "Sending..." : "Set password"}
          </Button>

          <SecurityNote
            className="mt-6"
            size="lg"
            align="left"
            text="Your information is encrypted and never sold."
          />
        </div>
      </div>
    </FullscreenLayout>
  );
};

export default WelcomeBackPage;
